// Fleet telemetry store — the I/O half of the heatmap.html port.
//
// Owns everything stateful the source page kept in globals and DOM inputs:
// the two sparkapi fetches, the 30s poll, and the control-panel values. All
// payload knowledge lives in $lib/fleet; this module only moves bytes and
// holds state.
//
// Both endpoints are public and CORS-open (verified 2026-08-17), so the browser
// calls them directly, exactly as heatmap.html does — no proxy, no auth header.

import {
  classify,
  countStates,
  deriveMotion,
  FLEET,
  type FleetCounts,
  type FleetUnit,
  type LogWatermarks,
  type MotionBuffers,
  rejectRegressive,
  normalizeBatteryPayload,
  normalizeCabinetPayload,
  type Seating,
  seatingMap,
  type Track,
  trackHeadings,
} from '$lib/fleet';
import type { Layers } from '$lib/fleetModes/types';

import { normalize, parse, same, type Viewport } from '$lib/viewport';
import { exponentialBackoff } from '$lib/backoff';
import { triggerPreferencesSync } from './persistenceCoordinator';
import { type Basemap, isBasemap } from './ui.svelte';
import { cabinets } from './cabinets.svelte';

/** Which heatmap is showing. Free-form so adding a mode touches only the
 *  fleetModes registry — an unknown key resolves to the fallback mode there. */
export type FleetModeKey = string;

export type { Layers };

/** Everything the control drawer edits — persisted verbatim in localStorage. */
export interface Controls {
  mode: FleetModeKey;
  basemap: Basemap;
  layers: Layers;
  dotSize: number;
  ringWidth: number;
  radius: number;
  blur: number;
  clusterRadius: number;
  clusterUntilZoom: number;
  zoomCoherent: boolean;
  /** Legend tier 2 (the pin key) open. Persisted because a reader who has
   *  learned the shapes should not have to re-open it on every visit, and one
   *  who hasn't should not meet it expanded. */
  pinKeyOpen: boolean;
  /** C3: draw a FLEET.SWAP_REACH_M circle around every cabinet holding
   *  rideable stock, turning "coverage gap" from a heat-colour judgement
   *  into a visible boundary. Off by default — it is a siting tool, not
   *  part of the map's resting state. */
  showReach: boolean;
  mapViewport?: Viewport;
}

/**
 * idle    — never loaded
 * loading — a request is in flight
 * ok      — last load succeeded
 * stale   — last load failed but we are still showing the previous snapshot
 */
type FleetStatus = 'idle' | 'loading' | 'ok' | 'stale';

const STORAGE_KEY = 'ph-fleet-controls';
const POLL_MS = 30_000; // heatmap.html's setInterval cadence
const MAX_BACKOFF_MS = 5 * 60_000;

const DEFAULTS: Controls = {
  mode: 'density',
  basemap: 'google-roadmap',
  layers: {
    moving: true,
    parked: true,
    cabinets: true,
    solo: false,
    spiro: false,
    arcride: false,
    solar: true,
  },
  dotSize: FLEET.DEFAULT_DOT_PX,
  ringWidth: FLEET.DEFAULT_RING_PX,
  radius: FLEET.DEFAULT_RADIUS_PX,
  blur: FLEET.DEFAULT_BLUR_PX,
  clusterRadius: FLEET.DEFAULT_CLUSTER_RADIUS_PX,
  clusterUntilZoom: FLEET.DEFAULT_CLUSTER_UNTIL_ZOOM,
  zoomCoherent: true,
  pinKeyOpen: false,
  showReach: false,
};

const ZERO: FleetCounts = { moving: 0, parked: 0, inCabinet: 0, solo: 0 };

/** Merge stored controls over the defaults, field by field, so a partial or
 *  corrupt blob (or one written by an older build) can't poison the panel. */
function readControls(): Controls {
  const base: Controls = { ...DEFAULTS, layers: { ...DEFAULTS.layers } };
  if (typeof window === 'undefined') return base;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Partial<Controls>;
    if (typeof saved.mode === 'string') base.mode = saved.mode;
    if (isBasemap(saved.basemap)) {
      base.basemap = saved.basemap;
    }
    if (typeof saved.zoomCoherent === 'boolean') base.zoomCoherent = saved.zoomCoherent;
    if (typeof saved.pinKeyOpen === 'boolean') base.pinKeyOpen = saved.pinKeyOpen;
    if (typeof saved.showReach === 'boolean') base.showReach = saved.showReach;
    for (const k of [
      'dotSize',
      'ringWidth',
      'radius',
      'blur',
      'clusterRadius',
      'clusterUntilZoom',
    ] as const) {
      if (typeof saved[k] === 'number' && Number.isFinite(saved[k])) base[k] = saved[k];
    }
    if (saved.layers && typeof saved.layers === 'object') {
      for (const k of [
        'moving',
        'parked',
        'cabinets',
        'solo',
        'spiro',
        'arcride',
        'solar',
      ] as const) {
        if (typeof saved.layers[k] === 'boolean') base.layers[k] = saved.layers[k];
      }
    }
    if (saved.mapViewport) {
      const parsedVp = parse(saved.mapViewport);
      if (parsedVp) base.mapViewport = parsedVp;
    }
  } catch {
    /* unreadable blob — defaults are fine */
  }
  return base;
}

function writeControls(c: Controls): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* private mode / quota — the panel still works, it just won't persist */
  }
}

/** Exponential backoff after consecutive failures, capped. */
export function nextDelay(failures: number): number {
  return exponentialBackoff(failures, POLL_MS, MAX_BACKOFF_MS);
}

interface FleetError extends Error {
  userMessage?: string;
}

async function fetchJson(url: string, signal: AbortSignal): Promise<unknown> {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    const e = new Error(`${new URL(url).pathname} → HTTP ${res.status}`) as FleetError;
    e.userMessage =
      res.status >= 500
        ? "Can't reach the fleet feed. Showing the last good snapshot."
        : 'Fleet data unavailable — check your connection or sign in again.';
    throw e;
  }
  return res.json();
}


function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

// ── State ─────────────────────────────────────────────────────────────────

let units = $state<FleetUnit[]>([]);
let counts = $state<FleetCounts>({ ...ZERO });
let rejected = $state(0);
let reference = $state(0);
let status = $state<FleetStatus>('idle');
let error = $state<string | null>(null);
/** True when the battery feed loaded but the cabinet feed did not, so seating
 *  is carried over from an earlier cycle and may be wrong. */
let degraded = $state(false);
let lastUpdated = $state<number | null>(null);
let controls = $state<Controls>(readControls());

// Which map pin has its popup open, as FleetPins' marker id. Deliberately not
// part of Controls: it belongs in the URL (so a popup can be linked to) but
// never in localStorage — a stale popup reopening on the next visit is noise.
let selectedPin = $state<string | null>(null);

// Non-reactive: an input to classify(), never rendered directly.
let seating: Seating = new Map();

// Travel bearings, derived by comparing each poll against the last (the feed
// carries speed but no heading). Deliberately NOT $state: it is rewritten in the
// same tick as `units`, and every consumer already re-renders on that, so making
// it reactive would only buy a second render of the same frame.
let headings = new Map<string, Track>();

// High-water mark of lastlogtime per serial, so a re-sent stale row (measured
// at 3 units of 335, up to 11 minutes back) doesn't flip a moving unit's
// freshness gate for one poll — see rejectRegressive.
let logWatermarks: LogWatermarks = new Map();

// Trailing per-unit position buffer for track-based motion — see deriveMotion.
let motionBuffers: MotionBuffers = new Map();

let inflight: AbortController | null = null;
let reqSeq = 0;
let failures = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

async function load(): Promise<void> {
  // A manual refresh supersedes an in-flight poll rather than racing it — the
  // seq check below also stops a late response from overwriting a newer one.
  inflight?.abort();
  const ac = new AbortController();
  inflight = ac;
  const seq = ++reqSeq;

  status = 'loading';
  try {
    // allSettled, not all: a cabinet outage must not blank the battery map.
    const [battRes, cabRes] = await Promise.allSettled([
      fetchJson(FLEET.BATTERY_API, ac.signal),
      fetchJson(FLEET.CABINET_API, ac.signal),
    ]);

    if (seq !== reqSeq) return; // superseded by a newer request

    if (battRes.status === 'rejected') throw battRes.reason;

    if (cabRes.status === 'fulfilled') {
      // Share this exact polled snapshot with cabinet pins, heat and reach
      // rings. This keeps cabinet metadata fresh without a second request or
      // divergent seating/coordinates between stores.
      const cabinetRows = normalizeCabinetPayload(cabRes.value);
      cabinets.ingest(cabRes.value);
      seating = seatingMap(cabinetRows);
      degraded = false;
    } else if (!isAbort(cabRes.reason)) {
      // Source page did this silently (`if (cabRes.ok)` with no else), so an
      // outage quietly reused last cycle's seating forever. Keep the carry-over
      // — dropping it would reclassify ~126 batteries as loose stock — but say so.
      degraded = true;
    }

    const rawRecords = normalizeBatteryPayload(battRes.value);


    const { records, watermarks } = rejectRegressive(rawRecords, logWatermarks);
    logWatermarks = watermarks;

    // Assign unconditionally, including for an empty batch. heatmap.html
    // returned early on `!rawBatteries.length` before clearing markers or
    // writing the KPIs, so an empty response left stale pins and stale numbers
    // sitting under a green "Live Streams Synced" banner.
    const result = classify(records, seating);
    // Before `units` is replaced: headings compare the new batch against the
    // positions the previous one was measured from.
    headings = trackHeadings(result.units, headings);
    const motion = deriveMotion(result.units, motionBuffers, Date.now());
    motionBuffers = motion.buffers;
    units = motion.units;
    counts = countStates(motion.units);
    rejected = result.rejected;
    reference = result.reference;

    lastUpdated = Date.now();
    error = null;
    failures = 0;
    status = 'ok';
  } catch (err) {
    if (isAbort(err) || seq !== reqSeq) return; // aborted or superseded: not a failure
    failures++;
    const fleetErr = err as FleetError;
    error = fleetErr?.userMessage ?? (err instanceof Error ? err.message : String(err));
    // Keep the last good snapshot on screen — a blank map is less useful than a
    // clearly-labelled stale one — but never under an "ok" status.
    status = 'stale';
    console.error('fleet.load failed:', err);
  } finally {
    if (inflight === ac) inflight = null;
  }
}

/** Load, then queue the next poll at the current backoff. */
async function cycle(): Promise<void> {
  await load();
  if (!started) return;
  clearTimer();
  timer = setTimeout(() => void cycle(), nextDelay(failures));
}

function onVisibility(): void {
  if (!started) return;
  if (document.hidden) {
    // Nothing to look at — stop burning ~300KB every 30s in a background tab.
    clearTimer();
    return;
  }
  const age = lastUpdated === null ? Number.POSITIVE_INFINITY : Date.now() - lastUpdated;
  if (age >= POLL_MS) void cycle();
  else {
    clearTimer();
    timer = setTimeout(() => void cycle(), POLL_MS - age);
  }
}

function persist(): void {
  writeControls(controls);
  triggerPreferencesSync();
}

export const fleet = {
  get units() {
    return units;
  },
  get counts() {
    return counts;
  },
  get rejected() {
    return rejected;
  },
  /** Feed clock: newest lastlogtime in the batch (ms). Runs ahead of real UTC. */
  get reference() {
    return reference;
  },
  get status() {
    return status;
  },
  get error() {
    return error;
  },
  get degraded() {
    return degraded;
  },
  get lastUpdated() {
    return lastUpdated;
  },
  get total() {
    return units.length;
  },
  /**
   * Derived travel bearing for a unit (degrees from north), or null when none
   * has been measured yet — which is every unit for the first poll or two after
   * a refresh, since `headings` is reset there. Callers must handle null rather
   * than falling back to 0; see Track.deg.
   */
  heading(id: string): number | null {
    return headings.get(id)?.deg ?? null;
  },

  // ── Controls ───────────────────────────────────────────────────────────
  get mode() {
    return controls.mode;
  },
  set mode(v: FleetModeKey) {
    controls.mode = v;
    persist();
  },
  get basemap() {
    return controls.basemap;
  },
  /** The open popup's marker id — set by FleetPins on popupopen/popupclose,
   *  and seeded from the URL's `sel` at mount so a link reopens it. */
  get selectedPin() {
    return selectedPin;
  },
  set selectedPin(v: string | null) {
    selectedPin = v;
  },
  set basemap(v: Basemap) {
    controls.basemap = v;
    persist();
  },
  get layers() {
    return controls.layers;
  },
  setLayer(key: keyof Layers, on: boolean) {
    controls.layers[key] = on;
    persist();
  },
  /** Applied when a mode is selected — each mode declares its own defaults. */
  setLayers(next: Layers) {
    controls.layers = { ...next };
    persist();
  },
  get dotSize() {
    return controls.dotSize;
  },
  set dotSize(v: number) {
    controls.dotSize = v;
    persist();
  },
  get ringWidth() {
    return controls.ringWidth;
  },
  set ringWidth(v: number) {
    controls.ringWidth = v;
    persist();
  },
  get radius() {
    return controls.radius;
  },
  set radius(v: number) {
    controls.radius = v;
    persist();
  },
  get blur() {
    return controls.blur;
  },
  set blur(v: number) {
    controls.blur = v;
    persist();
  },
  get clusterRadius() {
    return controls.clusterRadius;
  },
  set clusterRadius(v: number) {
    controls.clusterRadius = v;
    persist();
  },
  get clusterUntilZoom() {
    return controls.clusterUntilZoom;
  },
  set clusterUntilZoom(v: number) {
    controls.clusterUntilZoom = v;
    persist();
  },
  get zoomCoherent() {
    return controls.zoomCoherent;
  },
  set zoomCoherent(v: boolean) {
    controls.zoomCoherent = v;
    persist();
  },
  get pinKeyOpen() {
    return controls.pinKeyOpen;
  },
  set pinKeyOpen(v: boolean) {
    controls.pinKeyOpen = v;
    persist();
  },
  get showReach() {
    return controls.showReach;
  },
  set showReach(v: boolean) {
    controls.showReach = v;
    persist();
  },
  get mapViewport() {
    return controls.mapViewport;
  },
  setViewport(center: [number, number], zoom: number) {
    const next = normalize(center, zoom);
    if (same(controls.mapViewport, next)) return;
    controls.mapViewport = next;
    persist();
  },
  get rawControls(): Controls {
    return {
      ...controls,
      layers: { ...controls.layers },
      ...(controls.mapViewport ? { mapViewport: { ...controls.mapViewport } } : {}),
    };
  },
  applyControls(patch: Partial<Controls>): void {
    if (typeof patch.mode === 'string') controls.mode = patch.mode;
    if (isBasemap(patch.basemap)) {
      controls.basemap = patch.basemap;
    }
    if (typeof patch.zoomCoherent === 'boolean') controls.zoomCoherent = patch.zoomCoherent;
    if (typeof patch.pinKeyOpen === 'boolean') controls.pinKeyOpen = patch.pinKeyOpen;
    if (typeof patch.showReach === 'boolean') controls.showReach = patch.showReach;
    for (const k of [
      'dotSize',
      'ringWidth',
      'radius',
      'blur',
      'clusterRadius',
      'clusterUntilZoom',
    ] as const) {
      if (typeof patch[k] === 'number' && Number.isFinite(patch[k]))
        controls[k] = patch[k] as number;
    }
    if (patch.layers && typeof patch.layers === 'object') {
      for (const k of [
        'moving',
        'parked',
        'cabinets',
        'solo',
        'spiro',
        'arcride',
        'solar',
      ] as const) {
        if (typeof patch.layers[k] === 'boolean') controls.layers[k] = patch.layers[k] as boolean;
      }
    }
    if (patch.mapViewport) {
      const parsedVp = parse(patch.mapViewport);
      if (parsedVp) controls.mapViewport = parsedVp;
    }
    persist();
  },
  resetControls() {
    controls = { ...DEFAULTS, layers: { ...DEFAULTS.layers } };
    persist();
  },
  resetView() {
    controls = { ...DEFAULTS, layers: { ...DEFAULTS.layers } };
    selectedPin = null;
    if (typeof window !== 'undefined') {
      history.replaceState(null, '', window.location.pathname);
    }
    persist();
  },

  // ── Lifecycle ──────────────────────────────────────────────────────────
  /** Begin polling. Called from FleetView's $effect; safe to call twice. */
  start(): void {
    if (started) return;
    started = true;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    void cycle();
  },
  /** Stop polling and abort anything in flight. */
  stop(): void {
    started = false;
    clearTimer();
    inflight?.abort();
    inflight = null;
    // Drop tracked positions: on the next mount they would be an arbitrary
    // amount of time old, and a bearing across that gap is not a heading.
    headings = new Map();
    logWatermarks = new Map();
    motionBuffers = new Map();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  },
  /** Manual refresh — supersedes an in-flight poll and resets the backoff.
   *  Awaitable; only re-arms the poll timer if start() is active. */
  refresh(): Promise<void> {
    failures = 0;
    return cycle();
  },
};
